import { v4 as uuidv4 } from 'uuid';
import type { Tenant, TenantAdmin, TenantMember, TenantSchemaProfile, CreateTenantInput, AddTenantUserInput } from '../types/tenant';
import type { User, CreateUserInput } from '../types/user';
import type { SchemaProfile, CreateSchemaProfileInput } from '../types/schemaProfile';

function now(): string {
  return new Date().toISOString();
}

function delay<T>(value: T, ms = 200): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

const users: User[] = [
  {
    id: 'user-001',
    keycloakRealm: 'dc',
    name: '张三',
    email: 'zhangsan@example.com',
    username: 'zhangsan',
    displayName: '张三',
    isServiceAccount: false,
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-01-01T00:00:00.000Z',
  },
  {
    id: 'user-002',
    keycloakRealm: 'dc',
    name: '李四',
    email: 'lisi@example.com',
    username: 'lisi',
    displayName: '李四',
    isServiceAccount: false,
    createdAt: '2026-01-02T00:00:00.000Z',
    updatedAt: '2026-01-02T00:00:00.000Z',
  },
];

const tenants: Tenant[] = [
  {
    id: 'tenant-001',
    name: '研发平台',
    email: 'rd@example.com',
    code: 'rd_platform',
    type: 'ORGANIZATION',
    adminUserId: 'user-001',
    isPrimary: false,
    createdAt: '2026-01-10T00:00:00.000Z',
    updatedAt: '2026-01-10T00:00:00.000Z',
  },
];

const tenantAdmins: TenantAdmin[] = [
  { tenantId: 'tenant-001', userId: 'user-001', createdAt: '2026-01-10T00:00:00.000Z' },
];

const tenantMembers: TenantMember[] = [
  { tenantId: 'tenant-001', userId: 'user-002', createdAt: '2026-01-11T00:00:00.000Z' },
];

const tenantSchemaProfiles: TenantSchemaProfile[] = [
  {
    tenantId: 'tenant-001',
    schemaProfile: 'rd_platform',
    createdAt: '2026-01-10T00:00:00.000Z',
    updatedAt: '2026-01-10T00:00:00.000Z',
  },
];

const schemaProfiles: SchemaProfile[] = [
  {
    id: 'rd_platform',
    name: '研发平台 Schema',
    description: '研发平台默认数据模型',
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-01-01T00:00:00.000Z',
  },
  {
    id: 'default',
    name: '默认 Schema',
    description: '通用默认数据模型',
    createdAt: '2026-01-01T00:00:00.000Z',
    updatedAt: '2026-01-01T00:00:00.000Z',
  },
];

function findUserByEmail(email: string): User | undefined {
  return users.find((u) => u.email.toLowerCase() === email.toLowerCase());
}

export const mockStore = {
  async listTenants(): Promise<Tenant[]> {
    return delay([...tenants]);
  },

  async getTenant(id: string): Promise<Tenant | undefined> {
    return delay(tenants.find((t) => t.id === id));
  },

  async createTenant(input: CreateTenantInput): Promise<Tenant> {
    if (tenants.some((t) => t.code === input.code)) {
      throw new Error(`租户编码 "${input.code}" 已存在`);
    }
    const tenant: Tenant = {
      id: uuidv4(),
      ...input,
      isPrimary: false,
      createdAt: now(),
      updatedAt: now(),
    };
    tenants.push(tenant);
    return delay(tenant);
  },

  async listUsers(): Promise<User[]> {
    return delay([...users]);
  },

  async getUser(id: string): Promise<User | undefined> {
    return delay(users.find((u) => u.id === id));
  },

  async findOrCreateUser(input: CreateUserInput): Promise<{ user: User; created: boolean; keycloakCreated: boolean }> {
    const existing = findUserByEmail(input.email);
    if (existing) {
      return delay({ user: existing, created: false, keycloakCreated: false });
    }

    const user: User = {
      id: uuidv4(),
      keycloakRealm: 'dc',
      name: input.name,
      email: input.email,
      username: input.username,
      displayName: input.name,
      isServiceAccount: false,
      createdAt: now(),
      updatedAt: now(),
    };
    users.push(user);
    return delay({
      user,
      created: true,
      keycloakCreated: Boolean(input.createInKeycloak),
    });
  },

  async getTenantAdmins(tenantId: string): Promise<TenantAdmin[]> {
    return delay(tenantAdmins.filter((a) => a.tenantId === tenantId));
  },

  async getTenantMembers(tenantId: string): Promise<TenantMember[]> {
    return delay(tenantMembers.filter((m) => m.tenantId === tenantId));
  },

  async addTenantUser(input: AddTenantUserInput): Promise<{ user: User; created: boolean; keycloakCreated: boolean }> {
    const tenant = tenants.find((t) => t.id === input.tenantId);
    if (!tenant) {
      throw new Error('租户不存在');
    }

    const { user, created, keycloakCreated } = await mockStore.findOrCreateUser({
      email: input.email,
      username: input.username ?? input.email.split('@')[0],
      name: input.name,
      createInKeycloak: input.createInKeycloak,
    });

    const target = input.role === 'admin' ? tenantAdmins : tenantMembers;
    const exists = target.some((r) => r.tenantId === input.tenantId && r.userId === user.id);
    if (!exists) {
      target.push({ tenantId: input.tenantId, userId: user.id, createdAt: now() });
    }

    if (input.role === 'admin' && !tenant.adminUserId) {
      tenant.adminUserId = user.id;
      tenant.updatedAt = now();
    }

    return delay({ user, created, keycloakCreated });
  },

  async removeTenantAdmin(tenantId: string, userId: string): Promise<void> {
    const idx = tenantAdmins.findIndex((a) => a.tenantId === tenantId && a.userId === userId);
    if (idx >= 0) {
      tenantAdmins.splice(idx, 1);
    }
    const tenant = tenants.find((t) => t.id === tenantId);
    if (tenant?.adminUserId === userId) {
      tenant.adminUserId = tenantAdmins.find((a) => a.tenantId === tenantId)?.userId;
      tenant.updatedAt = now();
    }
    return delay(undefined);
  },

  async removeTenantMember(tenantId: string, userId: string): Promise<void> {
    const idx = tenantMembers.findIndex((m) => m.tenantId === tenantId && m.userId === userId);
    if (idx >= 0) {
      tenantMembers.splice(idx, 1);
    }
    return delay(undefined);
  },

  async getTenantSchemaProfile(tenantId: string): Promise<TenantSchemaProfile | undefined> {
    return delay(tenantSchemaProfiles.find((b) => b.tenantId === tenantId));
  },

  async setTenantSchemaProfile(tenantId: string, schemaProfile: string): Promise<TenantSchemaProfile> {
    const profile = schemaProfiles.find((p) => p.id === schemaProfile);
    if (!profile) {
      throw new Error(`Schema Profile "${schemaProfile}" 不存在`);
    }

    const existing = tenantSchemaProfiles.find((b) => b.tenantId === tenantId);
    if (existing) {
      existing.schemaProfile = schemaProfile;
      existing.updatedAt = now();
      return delay(existing);
    }

    const binding: TenantSchemaProfile = {
      tenantId,
      schemaProfile,
      createdAt: now(),
      updatedAt: now(),
    };
    tenantSchemaProfiles.push(binding);
    return delay(binding);
  },

  async clearTenantSchemaProfile(tenantId: string): Promise<void> {
    const idx = tenantSchemaProfiles.findIndex((b) => b.tenantId === tenantId);
    if (idx >= 0) {
      tenantSchemaProfiles.splice(idx, 1);
    }
    return delay(undefined);
  },

  async listSchemaProfiles(): Promise<SchemaProfile[]> {
    return delay([...schemaProfiles]);
  },

  async getSchemaProfile(id: string): Promise<SchemaProfile | undefined> {
    return delay(schemaProfiles.find((p) => p.id === id));
  },

  async createSchemaProfile(input: CreateSchemaProfileInput): Promise<SchemaProfile> {
    if (schemaProfiles.some((p) => p.id === input.id)) {
      throw new Error(`Schema Profile ID "${input.id}" 已存在`);
    }
    const profile: SchemaProfile = {
      id: input.id,
      name: input.name,
      description: input.description,
      createdAt: now(),
      updatedAt: now(),
    };
    schemaProfiles.push(profile);
    return delay(profile);
  },
};
